import React, { useMemo, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useAuthorization } from 'hooks';
import { useAssetHierarchyRules } from '../hooks/useAssetHierarchyRules';
import { get, set } from '@totalsoft/change-tracking-react';
import { emptyArray, emptyString, engineTypeCodes } from 'constants/defaultValues';
import { valueOrDefault } from 'utils/functions';
import { Grid, Chip } from '@mui/material';
import DriveEtaIcon from '@mui/icons-material/DriveEta';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import defaultAssetImage from 'assets/img/Car2.png';
import InsuranceDataContext from 'features/insuranceDocument/stepper/insuranceDataContext';
import AuthorizationCodes from 'features/common/constants/authorizationCodes';
import { Autocomplete, TextField } from '@totalsoft/rocket-ui';
import { DateTime } from '@ts-private/rocket-extensions';
import { onTextBoxChange } from 'utils/propertyChangeAdapters';
import { AssetControl, AssetImage, AssetImageContainer, VerticalAssetContainer } from './AssetStyles';

const AssetComponent = ({
  assetLens,
  loadingStatus,
  insuranceTypeCode,
  rcaInsuranceTypeCode,
  cascoInsuranceTypeCode,
  currencyCode,
  engineTypes,
  assetTypes,
  registrationTypes,
  assetCategories,
  assetSubcategories,
  vehicleCategories,
  hierarchy,
  hierarchyLevelsSources,
  onCategoryChange,
  onAssetTypeChange,
  onHierarchyLevelChange,
  onOtherAssetNameChange,
  onChassisNumberChange,
  onAssetPropertyBlur,
  validation,
  validateCascoProperties,
  readOnly
}) => {
  const { documentTypeId, documentStateId } = useContext(InsuranceDataContext);
  const hasRightToEditVehicle = useAuthorization(AuthorizationCodes.hasRightToEditVehicle, documentTypeId, documentStateId);
  const { t } = useTranslation();
  const {
    getHierarchyLevels,
    checkIfLevelIsDisabled,
    getHierarchyLevelDataSource,
    getHierarchyLevelDataSourceItem,
    getLevelError,
    getLevelHelperText
  } = useAssetHierarchyRules(hierarchy, hierarchyLevelsSources, assetLens.assetTypeId |> get);

  const hasOther = useMemo(() => Boolean(assetLens.otherAssetName |> get), [assetLens]);

  const handleHierarchyLevelChange = useCallback(
    level => selection => onHierarchyLevelChange(getHierarchyLevelDataSourceItem(level.level, selection?.id), level),
    [onHierarchyLevelChange, getHierarchyLevelDataSourceItem]
  );

  const optionEqualsValueAutocomplete = useCallback((option, value) => option.id === value?.id, []);

  return (
    <Grid container>
      <Grid item container xs={12} lg={8}>
        <VerticalAssetContainer item container xs={12} md={6}>
          <AssetControl item xs={12}>
            <TextField
              id='Asset_ChassisNumber'
              fullWidth
              required={insuranceTypeCode === rcaInsuranceTypeCode ? true : validateCascoProperties}
              label={t('Offer.Edit.Asset.ChassisNumber')}
              value={assetLens.chassisNumber |> get |> valueOrDefault(emptyString)}
              onChange={onTextBoxChange(onChassisNumberChange, { uppercase: true, rejectWhitespace: true })}
              disabled={readOnly('Asset.ChassisNumber', hasRightToEditVehicle)}
              error={!isValid(validation.chassisNumber)}
              helperText={getErrors(validation.chassisNumber)}
            />
          </AssetControl>
        </VerticalAssetContainer>
        <VerticalAssetContainer item container xs={12} md={6}>
          <AssetControl item xs={12}>
            <Autocomplete
              id={`Asset_Hierarchy_Type`}
              fullWidth
              required
              isSearchable
              isClearable={false}
              simpleValue
              label={t('Offer.Edit.Asset.AssetType')}
              value={assetLens.assetTypeId |> get |> valueOrDefault(emptyString)}
              onChange={onAssetTypeChange}
              disabled={loadingStatus.assetTypesLoading || readOnly('Asset.AssetType', hasRightToEditVehicle)}
              options={assetTypes || emptyArray}
              error={!isValid(validation.assetTypeId)}
              helperText={getErrors(validation.assetTypeId)}
            />
          </AssetControl>
          {hierarchy ? (
            getHierarchyLevels().map(level => {
              return (
                <AssetControl item key={level.level} xs={12}>
                  <Autocomplete
                    id={`Asset_Hierarchy_${level.level}`}
                    fullWidth
                    isSearchable
                    isClearable
                    simpleValue={false}
                    label={level.label}
                    value={level}
                    onChange={handleHierarchyLevelChange(level)}
                    disabled={checkIfLevelIsDisabled(level.level, hasOther) || readOnly('Asset.Hierarchy', hasRightToEditVehicle)}
                    options={getHierarchyLevelDataSource(level.level)}
                    error={getLevelError(level, validation, hasOther)}
                    helperText={getLevelHelperText(level, validation, hasOther)}
                    isOptionEqualToValue={optionEqualsValueAutocomplete}
                  />
                </AssetControl>
              );
            })
          ) : (
            <></>
          )}
        </VerticalAssetContainer>
      </Grid>
      <AssetImageContainer item xs={12} lg={4}>
        <AssetImage src={(assetLens.imageUrl |> get) || defaultAssetImage} alt='...' />
      </AssetImageContainer>
      {insuranceTypeCode === rcaInsuranceTypeCode && (
        <>
          <AssetControl item xs={12} md={4}>
            <Autocomplete
              id='Asset_Category'
              fullWidth
              required
              isSearchable={true}
              isClearable={false}
              simpleValue={true}
              disabled={
                loadingStatus.categoriesLoading ||
                loadingStatus.subcategoriesLoading ||
                readOnly('Asset.RCACategory', hasRightToEditVehicle)
              }
              label={t('Offer.Edit.Asset.RCACategory')}
              value={assetLens.rcaCategoryId |> get}
              onChange={onCategoryChange}
              options={assetCategories || emptyArray}
              error={!isValid(validation.rcaCategoryId)}
              helperText={getErrors(validation.rcaCategoryId)}
            />
          </AssetControl>
          <AssetControl item xs={12} md={4}>
            <Autocomplete
              id='Asset_Subcategory'
              fullWidth
              required
              isSearchable={true}
              isClearable={false}
              simpleValue={true}
              disabled={loadingStatus.subcategoriesLoading || readOnly('Asset.Subcategory', hasRightToEditVehicle)}
              label={t('Offer.Edit.Asset.Subcategory')}
              value={assetLens.subcategoryId |> get}
              onChange={assetLens.subcategoryId |> set}
              options={assetSubcategories || emptyArray}
              error={!isValid(validation.subcategoryId)}
              helperText={getErrors(validation.subcategoryId)}
            />
          </AssetControl>
          <Grid item xs={12} md={4}></Grid>
        </>
      )}
      {insuranceTypeCode !== rcaInsuranceTypeCode && (
        <>
          <AssetControl item xs={12} md={4}>
            <Autocomplete
              id='Asset_Category'
              fullWidth
              required
              isSearchable={true}
              isClearable={false}
              simpleValue={true}
              disabled={
                loadingStatus.categoriesLoading ||
                loadingStatus.subcategoriesLoading ||
                readOnly('Asset.CASCOCategory', hasRightToEditVehicle)
              }
              label={t('Offer.Edit.Asset.CASCOCategory')}
              value={assetLens.cascoCategoryId |> get}
              onChange={onCategoryChange}
              options={assetCategories || emptyArray}
              error={!isValid(validation.cascoCategoryId)}
              helperText={getErrors(validation.cascoCategoryId)}
            />
          </AssetControl>
          <AssetControl item xs={12} md={4}>
            <Autocomplete
              id='Asset_VehicleCategoryCode'
              fullWidth
              required={validateCascoProperties}
              isSearchable={true}
              isClearable={false}
              simpleValue={true}
              valueKey='code'
              labelKey='name'
              disabled={loadingStatus.vehicleCategoriesLoading || readOnly('Asset.VehicleCategoryCode', hasRightToEditVehicle)}
              label={t('Offer.Edit.Asset.VehicleCategoryCode')}
              value={assetLens.vehicleCategoryCode |> get}
              onChange={assetLens.vehicleCategoryCode |> set}
              options={vehicleCategories || emptyArray}
              error={!isValid(validation.vehicleCategoryCode)}
              helperText={getErrors(validation.vehicleCategoryCode)}
            />
          </AssetControl>
          <Grid item xs={12} md={4}></Grid>
        </>
      )}
      <AssetControl item xs={12} md={6} lg={4}>
        <TextField
          id='Asset_OtherAssetName'
          fullWidth
          label={t('Offer.Edit.Asset.OtherAssetName')}
          /* Only disable otherAssetName if it has no value and all levels are completed */
          disabled={(!hasOther && (assetLens.reverseLevel |> get) === 0) || readOnly('Asset.OtherAssetName', hasRightToEditVehicle)}
          value={assetLens.otherAssetName |> get |> valueOrDefault(emptyString)}
          onChange={onTextBoxChange(onOtherAssetNameChange, { uppercase: true })}
          error={!isValid(validation.otherAssetName)}
          helperText={getErrors(validation.otherAssetName)}
        />
      </AssetControl>
      <AssetControl item xs={12} md={6} lg={4}>
        <Autocomplete
          id='Asset_RegistrationType'
          fullWidth
          required
          isSearchable={true}
          isClearable={false}
          simpleValue={true}
          disabled={loadingStatus.registrationTypesLoading || readOnly('Asset.RegistrationType', hasRightToEditVehicle)}
          label={t('Offer.Edit.Asset.RegistrationType')}
          value={assetLens.registrationTypeId |> get}
          onChange={assetLens.registrationTypeId |> set}
          options={registrationTypes || emptyArray}
          error={!isValid(validation.registrationTypeId)}
          helperText={getErrors(validation.registrationTypeId)}
        />
      </AssetControl>
      <AssetControl item xs={12} md={6} lg={4}>
        <TextField
          id='Asset_RegistrationNumber'
          fullWidth
          label={t('Offer.Edit.Asset.RegistrationNumber')}
          value={assetLens.registrationNumber |> get |> valueOrDefault(emptyString)}
          onChange={onTextBoxChange(assetLens.registrationNumber |> set, { uppercase: true, rejectWhitespace: true })}
          disabled={readOnly('Asset.RegistrationNumber', hasRightToEditVehicle)}
          error={!isValid(validation.registrationNumber)}
          helperText={getErrors(validation.registrationNumber)}
        />
      </AssetControl>
      <AssetControl item xs={12} md={6} lg={4}>
        <Autocomplete
          id='Asset_EngineType'
          fullWidth
          required
          isSearchable={true}
          isClearable={false}
          simpleValue={true}
          valueKey='name'
          labelKey='name'
          disabled={loadingStatus.engineTypesLoading || readOnly('Asset.EngineType', hasRightToEditVehicle)}
          label={t('Offer.Edit.Asset.EngineType')}
          value={assetLens.engineType |> get}
          onChange={assetLens.engineType |> set}
          options={engineTypes.dictionary || emptyArray}
          error={!isValid(validation.engineType)}
          helperText={getErrors(validation.engineType)}
        />
      </AssetControl>
      <AssetControl item xs={12} md={6} lg={4}>
        <TextField
          id='Asset_CylindricCapacity'
          isNumeric={true}
          fullWidth={true}
          required={true}
          inputProps={{ decimalScale: 0 }}
          label={t('Offer.Edit.Asset.CylindricCapacity')}
          value={assetLens.cylindricCapacity |> get |> valueOrDefault(emptyString)}
          onChange={assetLens.cylindricCapacity |> set}
          disabled={
            readOnly('Asset.CylindricCapacity', hasRightToEditVehicle) ||
            engineTypes.getEngineTypeCodeByName(assetLens.engineType |> get) === engineTypeCodes.ELECTRIC
          }
          error={!isValid(validation.cylindricCapacity)}
          helperText={getErrors(validation.cylindricCapacity)}
        />
      </AssetControl>
      <AssetControl item xs={12} md={6} lg={4}>
        <TextField
          id='Asset_EnginePower'
          isNumeric={true}
          fullWidth={true}
          required={true}
          inputProps={{ decimalScale: 0 }}
          label={t('Offer.Edit.Asset.EnginePower')}
          value={assetLens.enginePower |> get |> valueOrDefault(emptyString)}
          onChange={assetLens.enginePower |> set}
          disabled={readOnly('Asset.EnginePower', hasRightToEditVehicle)}
          error={!isValid(validation.enginePower)}
          helperText={getErrors(validation.enginePower)}
        />
      </AssetControl>
      <AssetControl item xs={12} md={6} lg={4}>
        <TextField
          id='Asset_MaxWeight'
          isNumeric={true}
          fullWidth={true}
          required={true}
          inputProps={{ decimalScale: 0 }}
          label={t('Offer.Edit.Asset.MaxWeight')}
          value={assetLens.maxWeight |> get |> valueOrDefault(emptyString)}
          onChange={assetLens.maxWeight |> set}
          onBlur={onAssetPropertyBlur}
          disabled={readOnly('Asset.MaxWeight')}
          error={!isValid(validation.maxWeight)}
          helperText={getErrors(validation.maxWeight)}
        />
      </AssetControl>
      <AssetControl item xs={12} md={6} lg={4}>
        <TextField
          id='Asset_CurrentMileage'
          isNumeric={true}
          fullWidth={true}
          required={true}
          inputProps={{ decimalScale: 0 }}
          label={t('Offer.Edit.Asset.CurrentMileage')}
          value={assetLens.currentMileage |> get |> valueOrDefault(emptyString)}
          onChange={assetLens.currentMileage |> set}
          disabled={readOnly('Asset.CurrentMileage', hasRightToEditVehicle)}
          error={!isValid(validation.currentMileage)}
          helperText={getErrors(validation.currentMileage)}
        />
      </AssetControl>
      <AssetControl item xs={12} md={6} lg={4}>
        <TextField
          id='Asset_SeatsNumber'
          isNumeric={true}
          fullWidth={true}
          required={true}
          inputProps={{ decimalScale: 0 }}
          label={t('Offer.Edit.Asset.SeatsNumber')}
          value={assetLens.seatsNumber |> get |> valueOrDefault(emptyString)}
          onChange={assetLens.seatsNumber |> set}
          onBlur={onAssetPropertyBlur}
          disabled={readOnly('Asset.SeatsNumber', hasRightToEditVehicle)}
          error={!isValid(validation.seatsNumber)}
          helperText={getErrors(validation.seatsNumber)}
        />
      </AssetControl>
      <AssetControl item xs={12} md={6} lg={4}>
        <TextField
          id='Asset_ManufacturingYear'
          isNumeric={true}
          fullWidth={true}
          required={true}
          inputProps={{ decimalScale: 0, thousandSeparator: false }}
          label={t('Offer.Edit.Asset.ManufacturingYear')}
          value={assetLens.manufacturingYear |> get |> valueOrDefault(emptyString)}
          onChange={assetLens.manufacturingYear |> set}
          disabled={readOnly('Asset.ManufacturingYear', hasRightToEditVehicle)}
          error={!isValid(validation.manufacturingYear)}
          helperText={getErrors(validation.manufacturingYear)}
        />
      </AssetControl>
      {insuranceTypeCode === cascoInsuranceTypeCode && (
        <AssetControl item xs={12} md={6} lg={4}>
          <TextField
            id='Asset_ManufacturingMonth'
            isNumeric={true}
            fullWidth={true}
            required={true}
            inputProps={{ decimalScale: 0 }}
            label={t('Offer.Edit.Asset.ManufacturingMonth')}
            value={assetLens.manufacturingMonth |> get |> valueOrDefault(emptyString)}
            onChange={assetLens.manufacturingMonth |> set}
            disabled={readOnly('Asset.ManufacturingMonth', hasRightToEditVehicle)}
            error={!isValid(validation.manufacturingMonth)}
            helperText={getErrors(validation.manufacturingMonth)}
          />
        </AssetControl>
      )}
      {insuranceTypeCode === rcaInsuranceTypeCode && (
        <AssetControl item xs={12} md={6} lg={4}>
          <TextField
            id='Asset_IdentityCardSeries'
            fullWidth
            required
            label={t('Offer.Edit.Asset.IdentityCardSeries')}
            value={assetLens.identityCardSeries |> get |> valueOrDefault(emptyString)}
            onChange={onTextBoxChange(assetLens.identityCardSeries |> set, {
              uppercase: true,
              rejectWhitespace: true
            })}
            disabled={readOnly('Asset.IdentityCardSeries', hasRightToEditVehicle)}
            error={!isValid(validation.identityCardSeries)}
            helperText={getErrors(validation.identityCardSeries)}
            inputProps={{
              maxLength: 7
            }}
          />
        </AssetControl>
      )}
      {insuranceTypeCode === cascoInsuranceTypeCode && (
        <>
          <AssetControl item xs={12} md={6} lg={4}>
            <TextField
              id='Asset_PurchasePrice'
              isNumeric={true}
              fullWidth={true}
              required={true}
              label={t('Offer.Edit.Asset.PurchasePrice', {
                currency: currencyCode
              })}
              value={assetLens.purchasePrice |> get |> valueOrDefault(emptyString)}
              onChange={assetLens.purchasePrice |> set}
              disabled={readOnly('Asset.PurchasePrice', hasRightToEditVehicle)}
              error={!isValid(validation.purchasePrice)}
              helperText={getErrors(validation.purchasePrice)}
            />
          </AssetControl>
          <AssetControl item xs={12} md={6} lg={4}>
            <DateTime
              id='Asset_PurchaseInvoiceDate'
              fullWidth
              required={validateCascoProperties}
              label={t('Offer.Edit.Asset.PurchaseInvoiceDate')}
              onChange={assetLens.purchaseInvoiceDate |> set}
              value={assetLens.purchaseInvoiceDate |> get}
              timeFormat={false}
              disabled={readOnly('Asset.PurchaseInvoiceDate', hasRightToEditVehicle)}
              error={!isValid(validation.purchaseInvoiceDate)}
              helperText={getErrors(validation.purchaseInvoiceDate)}
            />
          </AssetControl>
          <AssetControl item xs={12} md={6} lg={4}>
            <DateTime
              id='Asset_FirstRegistrationDate'
              fullWidth
              required={validateCascoProperties}
              label={t('Offer.Edit.Asset.FirstRegistrationDate')}
              onChange={assetLens.firstRegistrationDate |> set}
              value={assetLens.firstRegistrationDate |> get}
              timeFormat={false}
              disabled={readOnly('Asset.FirstRegistrationDate', hasRightToEditVehicle)}
              error={!isValid(validation.firstRegistrationDate)}
              helperText={getErrors(validation.firstRegistrationDate)}
            />
          </AssetControl>
          {insuranceTypeCode === cascoInsuranceTypeCode && (
            <Grid item container xs={12} md={4} alignContent='flex-end' justifyContent='flex-end'>
              <Chip
                icon={<DriveEtaIcon />}
                label={t('Offer.Edit.Asset.Quantity', {
                  value: assetLens.qtty |> get |> valueOrDefault(emptyString)
                })}
              />
            </Grid>
          )}
        </>
      )}
    </Grid>
  );
};

AssetComponent.propTypes = {
  assetLens: PropTypes.object.isRequired,
  loadingStatus: PropTypes.object.isRequired,
  insuranceTypeCode: PropTypes.string.isRequired,
  rcaInsuranceTypeCode: PropTypes.string,
  cascoInsuranceTypeCode: PropTypes.string,
  currencyCode: PropTypes.string,
  engineTypes: PropTypes.object,
  assetTypes: PropTypes.array,
  registrationTypes: PropTypes.array,
  assetCategories: PropTypes.array,
  assetSubcategories: PropTypes.array,
  vehicleCategories: PropTypes.array,
  hierarchy: PropTypes.array,
  hierarchyLevelsSources: PropTypes.object,
  onCategoryChange: PropTypes.func.isRequired,
  onAssetTypeChange: PropTypes.func.isRequired,
  onHierarchyLevelChange: PropTypes.func.isRequired,
  onOtherAssetNameChange: PropTypes.func.isRequired,
  onChassisNumberChange: PropTypes.func.isRequired,
  onAssetPropertyBlur: PropTypes.func.isRequired,
  validation: PropTypes.object,
  validateCascoProperties: PropTypes.bool.isRequired,
  readOnly: PropTypes.func.isRequired
};

export default AssetComponent;
